import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AccountDropdown from '@/components/layout/AccountDropdown';
import { LanguageSwitcher } from '@/components/ui';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { useI18n } from '@/hooks/useI18n';
import { useTheme } from '@/hooks/useTheme';
import { useTranslation } from 'react-i18next';
// Native MUI Components
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box
} from '@mui/material';
import { 
  ArrowBack, 
  Login, 
  MoreVert, 
  LightMode, 
  DarkMode, 
  SettingsBrightness,
  Language 
} from '@mui/icons-material';
import { useIsMobile } from '@/hooks/use-mobile';

export interface ActionItem {
  label: string;
  icon: React.ElementType; // Expect a component type
  onClick: () => void;
  iconOnly?: boolean; // If true, render only as icon button without label
}

interface HeaderProps {
  title: string;
  onBack?: () => void;
  actions?: ActionItem[];
}

const Header: React.FC<HeaderProps> = ({ title, onBack, actions = [] }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const isMobile = useIsMobile();
  const { mode, isDark, toggleTheme } = useTheme();
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const renderActions = () => {
    const allActions = [...actions]; // Start with parent actions

    // Get theme icon based on current mode
    const getThemeIcon = () => {
      if (mode === 'system') return SettingsBrightness;
      return isDark ? DarkMode : LightMode;
    };

    // Add theme toggle and language switcher as actions
    allActions.push({
      label: t('theme.toggle') || 'Theme',
      icon: getThemeIcon(),
      onClick: () => toggleTheme(),
      iconOnly: true,
    });

    allActions.push({
      label: t('language.switch') || 'Language',
      icon: Language,
      onClick: () => {
        // Cycle through languages
        const currentLang = i18n.language;
        const newLang = currentLang === 'de' ? 'en' : 'de';
        i18n.changeLanguage(newLang);
      },
      iconOnly: true,
    });

    // If user is not logged in, add the login action
    if (!currentUser) {
      allActions.push({
        label: t('auth.adminLogin'),
        icon: Login,
        onClick: () => navigate('/login'),
      });
    }

    if (allActions.length === 0) {
      return null;
    }

    if (isMobile) {
      return (
        <>
            <IconButton
            onClick={handleMenuClick}
            size="small"
            color="inherit"
            aria-controls={open ? 'header-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            >
            <MoreVert />
            </IconButton>
          <Menu
            id="header-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            {allActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <MenuItem key={index} onClick={action.onClick}>
                  <Icon style={{ marginRight: 8, width: 16, height: 16 }} />
                  {action.label}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {allActions.map((action, index) => {
          const Icon = action.icon;
          
          if (action.iconOnly) {
            return (
              <IconButton key={index} size="small" color="inherit" onClick={action.onClick}>
                <Icon />
              </IconButton>
            );
          }
          
          return (
            <Button key={index} size="small" color="inherit" variant='text' onClick={action.onClick} startIcon={<Icon />}>
              {action.label}
            </Button>
          );
        })}
      </Box>
    );
  };

  return (
    <AppBar 
      position="sticky" 
      elevation={1}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {onBack && (
            <IconButton onClick={onBack} size="small" edge="start" color="inherit">
              <ArrowBack />
            </IconButton>
          )}
          <Typography variant="h3" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {renderActions()}
          {currentUser && <AccountDropdown />}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
